'use client';

import React from 'react';
import Image from 'next/image';
import { heroVersions } from '@/static/hero';
import { HeroVersion } from '@/types/hero';
import TypingText from './typing-text';
import { useSearchParams } from 'next/navigation';
import DownloadButtons from './download-buttons';
import { useAdjustTrackingLink } from '@/hooks/use-adjust-qr';
import { getAdjustTrackingLink } from '@/lib/adjust-tracking';
import QRCode from '@/components/QR';
import { NEXT_PUBLIC_IMAGE_URL } from '@/common';
import CloseIcon from '@upzi/web-shell/icons/close-icon';
import SmartphoneIcon from '@upzi/web-shell/icons/smartphone-icon';
import { Button } from '@upzi/web-shell/ui/button';

const HERO_QR_DISMISSED_UNTIL_STORAGE_KEY = 'hero-qr-dismissed-until:v1';
const HERO_QR_DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

interface HeroContentProps {
  version: HeroVersion;
  variant: string;
}

const HeroContent: React.FC<HeroContentProps> = ({ version, variant }) => {
  const urlVersion = variant;

  return (
    <section
      id="hero-stage"
      className="relative flex items-center justify-center overflow-hidden bg-[#561caf] px-5 pt-32 pb-0 md:pt-40"
    >
      <div className="absolute right-0 bottom-0 left-0 h-40 from-transparent" />

      <div className="relative mx-auto w-full max-w-[1200px]">
        {/* Pattern 1 - Left Side */}
        <div className="absolute top-[10%] left-[-100px] z-10 md:top-[48%] md:left-[-80px] md:z-20 lg:top-[48%] lg:left-[100px] lg:z-10">
          <svg
            className="scale-50 md:scale-100"
            width="200"
            height="300"
            viewBox="0 0 200 300"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_16916_10204)">
              {/* Fill path - appears after stroke animation */}
              <path
                d="M191.422 0.595878C195.139 -0.296305 198.89 1.93543 199.8 5.58031C200.71 9.22517 198.434 12.9034 194.717 13.7957C182.104 16.8233 173.388 29.0872 169 46.1768C182.214 57.8613 190.801 74.0653 194.088 88.1357C195.859 95.7183 196.25 103.355 194.425 109.653C192.55 116.125 187.978 121.878 180.046 123.082L179.665 123.135C172.334 124.101 166.652 120.145 162.902 115.28C159.2 110.478 156.59 103.932 154.863 96.8713C151.68 83.8602 151.064 67.2947 153.706 51.6363C145.587 46.2456 136.01 43.1917 125.084 44.042L124.491 44.0915C109.161 45.4772 99.6451 51.8287 93.8537 60.555C88.5434 68.5568 85.9844 79.1522 85.8674 91.0888C109.279 99.5933 129.818 118.59 141.355 136.832C147.493 146.538 151.621 156.865 151.648 165.962C151.662 170.615 150.596 175.273 147.807 179.253C145.06 183.175 141.025 185.885 136.086 187.376L135.605 187.516C126.827 189.997 118.312 187.138 111.171 182.105C104.045 177.083 97.5515 169.441 92.0475 160.598C81.8531 144.22 74.2277 122.382 72.4164 101.329C60.5957 98.7903 48.7004 99.966 37.5814 106.544L36.9841 106.903C23.845 114.926 16.6629 127.333 14.5318 142.531C12.3752 157.912 15.4401 176.104 23.0649 194.744C38.2269 231.809 70.6141 268.715 110.395 286.17L111.336 286.578L111.659 286.726C114.937 288.334 116.389 292.2 114.9 295.529C113.412 298.858 109.53 300.431 106.103 299.156L105.773 299.024L104.743 298.577C61.2297 279.487 26.5092 239.665 10.203 199.802C1.97006 179.676 -1.77203 159.05 0.803872 140.679C3.40546 122.126 12.4963 105.847 29.6591 95.3673L30.4449 94.8957C43.998 86.8834 58.3714 85.1414 72.1088 87.4332C72.6815 75.0221 75.6828 63.0204 82.2387 53.142C90.5397 40.6342 104.043 32.2932 123.219 30.56L124.005 30.4936C136.479 29.5252 147.586 32.4643 157.053 37.6986C162.513 20.6371 173.124 4.98818 191.422 0.595878ZM86.9203 106.251C89.3443 123.205 95.7221 140.417 103.876 153.518C108.806 161.438 114.146 167.466 119.259 171.07C124.356 174.662 128.486 175.386 131.768 174.459L132.23 174.32C134.468 173.605 135.671 172.577 136.385 171.558C137.195 170.403 137.798 168.645 137.79 166.002C137.774 160.507 135.097 152.722 129.577 143.994C120.302 129.328 104.52 114.364 86.9203 106.251ZM166.25 63.0021C165.377 74.0283 166.2 84.9592 168.339 93.7014C169.841 99.8407 171.864 104.37 173.957 107.085C176.002 109.737 177.276 109.739 177.821 109.668L178.041 109.633C179.142 109.433 180.279 108.756 181.095 105.939C182.046 102.656 182.065 97.5319 180.579 91.1702C178.438 82.0037 173.503 71.6631 166.25 63.0021Z"
                fill="#E0E722"
                className="animate-fill-after-stroke"
              />
              {/* Stroke path - drawn first */}
              <path
                d="M191.422 0.595878C195.139 -0.296305 198.89 1.93543 199.8 5.58031C200.71 9.22517 198.434 12.9034 194.717 13.7957C182.104 16.8233 173.388 29.0872 169 46.1768C182.214 57.8613 190.801 74.0653 194.088 88.1357C195.859 95.7183 196.25 103.355 194.425 109.653C192.55 116.125 187.978 121.878 180.046 123.082L179.665 123.135C172.334 124.101 166.652 120.145 162.902 115.28C159.2 110.478 156.59 103.932 154.863 96.8713C151.68 83.8602 151.064 67.2947 153.706 51.6363C145.587 46.2456 136.01 43.1917 125.084 44.042L124.491 44.0915C109.161 45.4772 99.6451 51.8287 93.8537 60.555C88.5434 68.5568 85.9844 79.1522 85.8674 91.0888C109.279 99.5933 129.818 118.59 141.355 136.832C147.493 146.538 151.621 156.865 151.648 165.962C151.662 170.615 150.596 175.273 147.807 179.253C145.06 183.175 141.025 185.885 136.086 187.376L135.605 187.516C126.827 189.997 118.312 187.138 111.171 182.105C104.045 177.083 97.5515 169.441 92.0475 160.598C81.8531 144.22 74.2277 122.382 72.4164 101.329C60.5957 98.7903 48.7004 99.966 37.5814 106.544L36.9841 106.903C23.845 114.926 16.6629 127.333 14.5318 142.531C12.3752 157.912 15.4401 176.104 23.0649 194.744C38.2269 231.809 70.6141 268.715 110.395 286.17L111.336 286.578L111.659 286.726C114.937 288.334 116.389 292.2 114.9 295.529C113.412 298.858 109.53 300.431 106.103 299.156L105.773 299.024L104.743 298.577C61.2297 279.487 26.5092 239.665 10.203 199.802C1.97006 179.676 -1.77203 159.05 0.803872 140.679C3.40546 122.126 12.4963 105.847 29.6591 95.3673L30.4449 94.8957C43.998 86.8834 58.3714 85.1414 72.1088 87.4332C72.6815 75.0221 75.6828 63.0204 82.2387 53.142C90.5397 40.6342 104.043 32.2932 123.219 30.56L124.005 30.4936C136.479 29.5252 147.586 32.4643 157.053 37.6986C162.513 20.6371 173.124 4.98818 191.422 0.595878ZM86.9203 106.251C89.3443 123.205 95.7221 140.417 103.876 153.518C108.806 161.438 114.146 167.466 119.259 171.07C124.356 174.662 128.486 175.386 131.768 174.459L132.23 174.32C134.468 173.605 135.671 172.577 136.385 171.558C137.195 170.403 137.798 168.645 137.79 166.002C137.774 160.507 135.097 152.722 129.577 143.994C120.302 129.328 104.52 114.364 86.9203 106.251ZM166.25 63.0021C165.377 74.0283 166.2 84.9592 168.339 93.7014C169.841 99.8407 171.864 104.37 173.957 107.085C176.002 109.737 177.276 109.739 177.821 109.668L178.041 109.633C179.142 109.433 180.279 108.756 181.095 105.939C182.046 102.656 182.065 97.5319 180.579 91.1702C178.438 82.0037 173.503 71.6631 166.25 63.0021Z"
                fill="none"
                stroke="#E0E722"
                strokeWidth="0.5"
                strokeDasharray="2000"
                strokeDashoffset="2000"
                className="animate-draw-stroke-delayed"
              />
            </g>
            <defs>
              <clipPath id="clip0_16916_10204">
                <rect width="200" height="300" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>

        {/* Pattern 2 - Right Side */}
        <div className="absolute top-[28%] right-[-100px] z-10 md:top-[38%] md:right-[-80px] md:z-20 lg:top-[48%] lg:right-[100px] lg:z-50">
          <svg
            className="scale-50 md:scale-100"
            width="200"
            height="80"
            viewBox="0 0 200 80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0_17489_12435)">
              <path
                d="M85.1974 38.3566C102.795 4.52414 137.604 -5.16007 164.343 2.43557C178.626 6.49301 190.977 15.6143 196.691 28.9623C202.462 42.4438 200.974 59.0719 189.935 77.0965L189.767 77.3543C187.979 79.9621 184.367 80.7856 181.546 79.1767C178.635 77.5158 177.669 73.8915 179.39 71.0815L179.832 70.3511C188.977 55.0494 189.275 42.5964 185.371 33.4759C181.348 24.0773 172.366 17.037 160.884 13.7754C138.03 7.28371 106.724 16.2852 93.0751 50.3597C92.3306 52.2182 90.6657 53.5897 88.647 54.0075C86.7545 54.3991 84.7901 53.9028 83.3418 52.6854L83.0587 52.433C75.4928 45.2184 69.0048 41.2708 63.4406 39.5817C58.0666 37.9505 53.2592 38.3273 48.5472 40.3667C43.6514 42.4857 38.5886 46.5268 33.2518 52.6577C30.2896 56.0606 27.3218 60.0149 24.3183 64.4762L43.8958 60.422C47.2027 59.7373 50.4586 61.7696 51.1683 64.961C51.8779 68.1525 49.7721 71.2947 46.4651 71.9796L13.0388 78.9027C11.426 79.2368 9.74116 78.9288 8.36676 78.0485C6.99249 77.1682 6.04444 75.7899 5.73687 74.226L0.108676 45.606L0.0576415 45.3059C-0.384801 42.2081 1.76404 39.2864 4.98331 38.6967C8.20238 38.1071 11.311 40.0656 12.0756 43.1041L12.1417 43.4019L14.8011 56.9266C17.7789 52.5749 20.7941 48.5917 23.8734 45.0542C29.8446 38.1946 36.3131 32.7066 43.5346 29.581C50.9397 26.3759 58.8362 25.7921 67.1151 28.3051C73.1004 30.1219 79.0992 33.4982 85.1974 38.3566Z"
                fill="#E0E722"
                className="animate-fill-after-stroke"
              />
              <path
                d="M85.1974 38.3566C102.795 4.52414 137.604 -5.16007 164.343 2.43557C178.626 6.49301 190.977 15.6143 196.691 28.9623C202.462 42.4438 200.974 59.0719 189.935 77.0965L189.767 77.3543C187.979 79.9621 184.367 80.7856 181.546 79.1767C178.635 77.5158 177.669 73.8915 179.39 71.0815L179.832 70.3511C188.977 55.0494 189.275 42.5964 185.371 33.4759C181.348 24.0773 172.366 17.037 160.884 13.7754C138.03 7.28371 106.724 16.2852 93.0751 50.3597C92.3306 52.2182 90.6657 53.5897 88.647 54.0075C86.7545 54.3991 84.7901 53.9028 83.3418 52.6854L83.0587 52.433C75.4928 45.2184 69.0048 41.2708 63.4406 39.5817C58.0666 37.9505 53.2592 38.3273 48.5472 40.3667C43.6514 42.4857 38.5886 46.5268 33.2518 52.6577C30.2896 56.0606 27.3218 60.0149 24.3183 64.4762L43.8958 60.422C47.2027 59.7373 50.4586 61.7696 51.1683 64.961C51.8779 68.1525 49.7721 71.2947 46.4651 71.9796L13.0388 78.9027C11.426 79.2368 9.74116 78.9288 8.36676 78.0485C6.99249 77.1682 6.04444 75.7899 5.73687 74.226L0.108676 45.606L0.0576415 45.3059C-0.384801 42.2081 1.76404 39.2864 4.98331 38.6967C8.20238 38.1071 11.311 40.0656 12.0756 43.1041L12.1417 43.4019L14.8011 56.9266C17.7789 52.5749 20.7941 48.5917 23.8734 45.0542C29.8446 38.1946 36.3131 32.7066 43.5346 29.581C50.9397 26.3759 58.8362 25.7921 67.1151 28.3051C73.1004 30.1219 79.0992 33.4982 85.1974 38.3566Z"
                fill="none"
                stroke="#E0E722"
                strokeWidth="0.8"
                strokeDasharray="1000"
                strokeDashoffset="1000"
                className="animate-draw-stroke-delayed"
              />
            </g>
            <defs>
              <clipPath id="clip0_17489_12435">
                <rect width="200" height="80" fill="white" />
              </clipPath>
            </defs>
          </svg>
        </div>

        <div className="text-center">
          <h1 className="mx-auto w-[95%] text-3xl text-white md:w-[80%] md:text-6xl md:leading-tight">
            <div className="flex min-h-[8rem] items-center justify-center md:min-h-[12rem]">
              <div className="leading-tight">
                {urlVersion === 'A' ? (
                  <TypingText
                    texts={version.slogan.map(s => s.text)}
                    className="text-white"
                    typingSpeed={80}
                    fadeSpeed={1500}
                    pauseTime={2500}
                  />
                ) : (
                  version.slogan.map((text, index) => (
                    <h2
                      className="mb-8"
                      key={index}
                      dangerouslySetInnerHTML={{ __html: text.text }}
                    />
                  ))
                )}
              </div>
            </div>
          </h1>

          {version.showDownloadButtons && <DownloadButtons />}
          <div className="animate-fade-in-down-delayed relative z-40 mx-auto max-w-[640px]">
            <Image
              src={`${NEXT_PUBLIC_IMAGE_URL}/image/landing-page/hero-banner-letter-ani.png`}
              width={640}
              height={480}
              quality={75}
              sizes="(max-width: 768px) 100vw, 640px"
              priority
              alt="app mockup"
              className="animate-letter-fade-in-down-delayed absolute left-0 z-20 mt-12"
            />
            <Image
              src={`${NEXT_PUBLIC_IMAGE_URL}/image/landing-page/hero-banner-ani.png`}
              width={640}
              height={480}
              quality={75}
              sizes="(max-width: 768px) 100vw, 640px"
              priority
              alt="app mockup"
              className="relative z-10 mx-auto mt-12 md:mt-16"
            />
          </div>
        </div>
      </div>

      {/* Fixed QR Code - Bottom Right Corner */}
      {version.showQRCode && (
        <div>
          <HeroQRCode />
          <MobileQRButton />
        </div>
      )}
    </section>
  );
};

const Hero: React.FC = () => {
  const searchParams = useSearchParams();
  const urlVersion = searchParams?.get('version')?.toUpperCase() || 'A';
  const variant = urlVersion === 'A' || urlVersion === 'B' ? urlVersion : 'A';
  const versionKey = `version${variant}` as 'versionA' | 'versionB';
  const version = heroVersions[0][versionKey];

  if (!version) {
    console.error(`Version ${variant} not found in heroVersions data`);

    return null;
  }

  return <HeroContent key={variant} version={version} variant={variant} />;
};

// Component để hiển thị QR Code với Adjust tracking support
function HeroQRCode() {
  const adjustLinkForQR = useAdjustTrackingLink();
  const [dismissedUntil, setDismissedUntil] = React.useState<
    number | null | undefined
  >(undefined);

  React.useEffect(() => {
    let storedDismissedUntil: number | null = null;

    try {
      const storedValue = window.localStorage.getItem(
        HERO_QR_DISMISSED_UNTIL_STORAGE_KEY
      );
      const parsedDismissedUntil = storedValue ? Number(storedValue) : null;

      if (
        parsedDismissedUntil &&
        Number.isFinite(parsedDismissedUntil) &&
        parsedDismissedUntil > Date.now()
      ) {
        storedDismissedUntil = parsedDismissedUntil;
      } else {
        window.localStorage.removeItem(HERO_QR_DISMISSED_UNTIL_STORAGE_KEY);
      }
    } catch {
      // Storage may be unavailable in private browsing or blocked contexts.
    }

    const timeoutId = window.setTimeout(
      () => setDismissedUntil(storedDismissedUntil),
      0
    );

    return () => window.clearTimeout(timeoutId);
  }, []);

  React.useEffect(() => {
    if (dismissedUntil == null) return;

    const remainingDuration = Math.max(0, dismissedUntil - Date.now());

    const timeoutId = window.setTimeout(() => {
      try {
        window.localStorage.removeItem(HERO_QR_DISMISSED_UNTIL_STORAGE_KEY);
      } catch {
        // Storage may be unavailable in private browsing or blocked contexts.
      }

      setDismissedUntil(null);
    }, remainingDuration);

    return () => window.clearTimeout(timeoutId);
  }, [dismissedUntil]);

  const handleDismiss = () => {
    const nextDismissedUntil = Date.now() + HERO_QR_DISMISS_DURATION_MS;

    try {
      window.localStorage.setItem(
        HERO_QR_DISMISSED_UNTIL_STORAGE_KEY,
        String(nextDismissedUntil)
      );
    } catch {
      // Keep the QR hidden for this session even when persistence is unavailable.
    }

    setDismissedUntil(nextDismissedUntil);
  };

  if (dismissedUntil !== null) return null;

  return (
    <div className="fixed right-6 bottom-6 z-50 hidden rounded-[12px] bg-white p-3 shadow-lg sm:block">
      <div className="flex items-center justify-between gap-2">
        <p className="text-primary text-md flex items-center gap-1 font-semibold">
          <SmartphoneIcon size={20} aria-hidden />
          Tải app
        </p>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-alias-outline -m-1"
          aria-label="Ẩn mã QR trong 24 giờ"
          onClick={handleDismiss}
        >
          <CloseIcon size={16} aria-hidden />
        </Button>
      </div>
      {/* QR Code cho desktop/tablet */}
      <div className="mt-3 flex justify-center">
        {adjustLinkForQR ? (
          // Generate QR code client-side từ Adjust link
          <QRCode
            value={adjustLinkForQR}
            size={100}
            responsive={false}
            level="M"
          />
        ) : (
          // Fallback: dùng QR code mặc định
          <Image
            src="/images/qr_code_landing_page.svg"
            width={100}
            height={180}
            quality={100}
            alt="QR Code"
            className="rounded-lg"
          />
        )}
      </div>
    </div>
  );
}

const appStoreUrl =
  'https://apps.apple.com/app/apple-store/id6744628248?pt=1608090&ct=Upzi_landing_page&mt=8';
const playStoreUrl =
  'https://play.google.com/store/apps/details?id=com.navigosgroup.upzi&referrer=utm_source%3DUpzi_landing_page';

type LegacyMobileWindow = Window & {
  MSStream?: unknown;
  opera?: string;
};

function getMobileDownloadTarget() {
  const searchParams = new URLSearchParams(window.location.search);
  const adjustLink = getAdjustTrackingLink(searchParams);

  if (adjustLink) {
    return { isAdjustLink: true, url: adjustLink };
  }

  const legacyWindow = window as LegacyMobileWindow;
  const userAgent =
    navigator.userAgent || navigator.vendor || legacyWindow.opera || '';
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !legacyWindow.MSStream;

  return {
    isAdjustLink: false,
    url: isIOS ? appStoreUrl : playStoreUrl,
  };
}

function MobileQRButton() {
  const handleClick = () => {
    const target = getMobileDownloadTarget();

    if (target.isAdjustLink) {
      window.location.href = target.url;

      return;
    }

    window.open(target.url, '_blank');
  };

  return (
    <div className="fixed bottom-6 left-1/2 z-50 block w-fit -translate-x-1/2 sm:hidden">
      <button
        onClick={handleClick}
        aria-label="Tải về từ Store"
        style={{ background: 'none', border: 'none', padding: 0 }}
      >
        <svg
          width="182"
          height="97"
          viewBox="0 0 182 97"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g filter="url(#filter0_dd_18257_11551)">
            <path
              d="M18 34.5C18 23.4543 26.9543 14.5 38 14.5H144C155.046 14.5 164 23.4543 164 34.5V54.5C164 65.5457 155.046 74.5 144 74.5H38C26.9543 74.5 18 65.5457 18 54.5V34.5Z"
              fill="white"
            />
            <path
              d="M22 34.5C22 25.6634 29.1634 18.5 38 18.5H144C152.837 18.5 160 25.6634 160 34.5V54.5C160 63.3366 152.837 70.5 144 70.5H38C29.1634 70.5 22 63.3366 22 54.5V34.5Z"
              fill="#9447FF"
            />
            <path
              d="M57.833 31.667C59.766 31.667 61.333 33.234 61.333 35.167V53.833C61.333 55.766 59.766 57.333 57.833 57.333H46.167C44.234 57.333 42.667 55.766 42.667 53.833V35.167C42.667 33.234 44.234 31.667 46.167 31.667H57.833ZM46.167 34C45.5227 34 45 34.5227 45 35.167V53.833C45 54.4773 45.5227 55 46.167 55H57.833C58.4773 55 59 54.4773 59 53.833V35.167C59 34.5227 58.4773 34 57.833 34H46.167ZM52.0117 50.333C52.6559 50.3332 53.1777 50.8558 53.1777 51.5C53.1777 52.1442 52.6559 52.6668 52.0117 52.667H52C51.3557 52.667 50.833 52.1443 50.833 51.5C50.833 50.8557 51.3557 50.333 52 50.333H52.0117Z"
              fill="white"
            />
            <path
              d="M74.7159 40.397V38.4091H85.1605V40.397H81.1143V51.5H78.7621V40.397H74.7159ZM88.5611 51.6982C87.9389 51.6982 87.3786 51.5874 86.88 51.3658C86.3857 51.1399 85.9936 50.8075 85.7038 50.3686C85.4183 49.9297 85.2756 49.3885 85.2756 48.745C85.2756 48.1911 85.3778 47.733 85.5824 47.3707C85.7869 47.0085 86.0661 46.7188 86.4197 46.5014C86.7734 46.2841 87.1719 46.12 87.6151 46.0092C88.0625 45.8942 88.5249 45.8111 89.0021 45.7599C89.5774 45.7003 90.044 45.647 90.402 45.6001C90.7599 45.549 91.0199 45.4723 91.1818 45.37C91.348 45.2635 91.4311 45.0994 91.4311 44.8778V44.8395C91.4311 44.358 91.2884 43.9851 91.0028 43.7209C90.7173 43.4567 90.3061 43.3246 89.7692 43.3246C89.2024 43.3246 88.7528 43.4482 88.4205 43.6953C88.0923 43.9425 87.8707 44.2344 87.7557 44.571L85.5952 44.2642C85.7656 43.6676 86.0469 43.169 86.4389 42.7685C86.831 42.3636 87.3104 42.0611 87.8771 41.8608C88.4439 41.6562 89.0703 41.554 89.7564 41.554C90.2294 41.554 90.7003 41.6094 91.169 41.7202C91.6378 41.831 92.0661 42.0142 92.4538 42.2699C92.8416 42.5213 93.1527 42.8643 93.3871 43.299C93.6257 43.7337 93.745 44.277 93.745 44.929V51.5H91.5206V50.1513H91.4439C91.3033 50.424 91.1051 50.6797 90.8494 50.9183C90.598 51.1527 90.2805 51.3423 89.897 51.4872C89.5178 51.6278 89.0724 51.6982 88.5611 51.6982ZM89.1619 49.9979C89.6264 49.9979 90.0291 49.9062 90.37 49.723C90.7109 49.5355 90.973 49.2884 91.1562 48.9815C91.3438 48.6747 91.4375 48.3402 91.4375 47.978V46.821C91.3651 46.8807 91.2415 46.9361 91.0668 46.9872C90.8963 47.0384 90.7045 47.0831 90.4915 47.1214C90.2784 47.1598 90.0675 47.1939 89.8587 47.2237C89.6499 47.2536 89.4688 47.2791 89.3153 47.3004C88.9702 47.3473 88.6612 47.424 88.3885 47.5305C88.1158 47.6371 87.9006 47.7862 87.7429 47.978C87.5852 48.1655 87.5064 48.4084 87.5064 48.7067C87.5064 49.1328 87.6619 49.4545 87.973 49.6719C88.2841 49.8892 88.6804 49.9979 89.1619 49.9979ZM90.7472 40.3011H88.983L88.8935 39.0739C89.3452 39.0568 89.6648 38.9993 89.8523 38.9013C90.0398 38.799 90.1314 38.6584 90.1271 38.4794C90.1314 38.2493 90.0078 38.0895 89.7564 38C89.505 37.9105 89.1939 37.8658 88.8232 37.8658L88.8807 36.7216C89.9375 36.7216 90.7386 36.875 91.2841 37.1818C91.8295 37.4886 92.1001 37.9062 92.0959 38.4347C92.1001 38.8395 91.9616 39.142 91.6804 39.3423C91.3991 39.5384 91.0881 39.6619 90.7472 39.7131V40.3011ZM96.0717 51.5V41.6818H98.3857V51.5H96.0717ZM97.2351 40.2884C96.8686 40.2884 96.5533 40.1669 96.2891 39.924C96.0249 39.6768 95.8928 39.3807 95.8928 39.0355C95.8928 38.6861 96.0249 38.3899 96.2891 38.147C96.5533 37.8999 96.8686 37.7763 97.2351 37.7763C97.6058 37.7763 97.9212 37.8999 98.1811 38.147C98.4453 38.3899 98.5774 38.6861 98.5774 39.0355C98.5774 39.3807 98.4453 39.6768 98.1811 39.924C97.9212 40.1669 97.6058 40.2884 97.2351 40.2884ZM113.584 38.4091H115.956V46.9616C115.956 47.8991 115.734 48.7237 115.291 49.4354C114.852 50.147 114.234 50.7031 113.437 51.1037C112.64 51.5 111.709 51.6982 110.644 51.6982C109.574 51.6982 108.641 51.5 107.844 51.1037C107.047 50.7031 106.43 50.147 105.991 49.4354C105.552 48.7237 105.332 47.8991 105.332 46.9616V38.4091H107.704V46.7635C107.704 47.3089 107.823 47.7947 108.062 48.2209C108.305 48.647 108.645 48.9815 109.084 49.2244C109.523 49.4631 110.043 49.5824 110.644 49.5824C111.245 49.5824 111.765 49.4631 112.204 49.2244C112.647 48.9815 112.988 48.647 113.226 48.2209C113.465 47.7947 113.584 47.3089 113.584 46.7635V38.4091ZM118.431 55.1818V41.6818H120.707V43.3054H120.841C120.96 43.0668 121.129 42.8132 121.346 42.5447C121.563 42.272 121.857 42.0398 122.228 41.848C122.599 41.652 123.072 41.554 123.647 41.554C124.406 41.554 125.089 41.7479 125.699 42.1357C126.313 42.5192 126.798 43.0881 127.156 43.8423C127.518 44.5923 127.7 45.5128 127.7 46.6037C127.7 47.6818 127.523 48.598 127.169 49.3523C126.815 50.1065 126.334 50.6818 125.724 51.0781C125.115 51.4744 124.425 51.6726 123.653 51.6726C123.091 51.6726 122.624 51.5788 122.254 51.3913C121.883 51.2038 121.585 50.978 121.359 50.7138C121.137 50.4453 120.964 50.1918 120.841 49.9531H120.745V55.1818H118.431ZM120.7 46.5909C120.7 47.2259 120.79 47.782 120.969 48.2592C121.152 48.7365 121.414 49.1094 121.755 49.3778C122.1 49.642 122.518 49.7741 123.008 49.7741C123.519 49.7741 123.947 49.6378 124.293 49.3651C124.638 49.0881 124.898 48.7109 125.072 48.2337C125.251 47.7521 125.341 47.2045 125.341 46.5909C125.341 45.9815 125.254 45.4403 125.079 44.9673C124.904 44.4943 124.644 44.1236 124.299 43.8551C123.954 43.5866 123.523 43.4524 123.008 43.4524C122.513 43.4524 122.094 43.5824 121.749 43.8423C121.403 44.1023 121.141 44.4666 120.962 44.9354C120.788 45.4041 120.7 45.956 120.7 46.5909ZM129.553 51.5V50.0298L134.616 43.6825V43.5994H129.719V41.6818H137.447V43.2607L132.628 49.4993V49.5824H137.614V51.5H129.553ZM139.789 51.5V41.6818H142.102V51.5H139.789ZM140.952 40.2884C140.585 40.2884 140.27 40.1669 140.006 39.924C139.742 39.6768 139.61 39.3807 139.61 39.0355C139.61 38.6861 139.742 38.3899 140.006 38.147C140.27 37.8999 140.585 37.7763 140.952 37.7763C141.323 37.7763 141.638 37.8999 141.898 38.147C142.162 38.3899 142.294 38.6861 142.294 39.0355C142.294 39.3807 142.162 39.6768 141.898 39.924C141.638 40.1669 141.323 40.2884 140.952 40.2884Z"
              fill="white"
            />
          </g>
          <defs>
            <filter
              id="filter0_dd_18257_11551"
              x="0"
              y="0.5"
              width="182"
              height="96"
              filterUnits="userSpaceOnUse"
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix" />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feOffset dy="2" />
              <feGaussianBlur stdDeviation="4" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
              />
              <feBlend
                mode="normal"
                in2="BackgroundImageFix"
                result="effect1_dropShadow_18257_11551"
              />
              <feColorMatrix
                in="SourceAlpha"
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                result="hardAlpha"
              />
              <feMorphology
                radius="2"
                operator="dilate"
                in="SourceAlpha"
                result="effect2_dropShadow_18257_11551"
              />
              <feOffset dy="4" />
              <feGaussianBlur stdDeviation="8" />
              <feComposite in2="hardAlpha" operator="out" />
              <feColorMatrix
                type="matrix"
                values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"
              />
              <feBlend
                mode="normal"
                in2="effect1_dropShadow_18257_11551"
                result="effect2_dropShadow_18257_11551"
              />
              <feBlend
                mode="normal"
                in="SourceGraphic"
                in2="effect2_dropShadow_18257_11551"
                result="shape"
              />
            </filter>
          </defs>
        </svg>
      </button>
    </div>
  );
}

export default Hero;
